import React, { useState, useEffect } from "react";
import { ShoppingBag, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

export default function Bestilling() {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [formStatus, setFormStatus] = useState("idle"); // idle, submitting, success, error

  useEffect(() => {
    fetch("https://api.dev.auxo.dk/public/content/menu_items")
      .then((res) => res.json())
      .then((data) => {
        setMenuItems(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch menu items", err);
        setLoading(false);
      });
  }, []);

  const handleQuantityChange = (itemId, type, delta) => {
    const key = `${itemId}-${type}`;
    setQuantities((prev) => {
      const current = prev[key] || 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: next };
    });
  };

  const selectedItems = [];
  Object.entries(quantities).forEach(([key, qty]) => {
    if (qty <= 0) return;
    const lastDashIndex = key.lastIndexOf("-");
    const itemId = key.substring(0, lastDashIndex);
    const type = key.substring(lastDashIndex + 1);
    const item = menuItems.find((m) => m.id === itemId);
    if (item) {
      let price = item.price;
      let displayName = item.name;
      if (item.category === "Smørrebrød") {
        if (type === "luksus") {
          price = 58;
          displayName = `${item.name} (Luksus)`;
        } else {
          const isFish = item.name.toLowerCase().includes("fisk");
          price = isFish ? 28 : 24;
          displayName = `${item.name} (Håndmad)`;
        }
      }
      selectedItems.push({
        key,
        id: item.id,
        name: displayName,
        price,
        quantity: qty,
      });
    }
  });

  const totalPrice = selectedItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) {
      alert("Du skal vælge mindst én vare for at bestille.");
      return;
    }

    setFormStatus("submitting");

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Format the order details with two spaces at the end of each line to prevent Outlook/mail clients from collapsing them
    const orderDetails = selectedItems
      .map((item) => `${item.quantity}x ${item.name} (${item.price} kr.)  `)
      .join("\n");

    const { bestilling, ...restData } = data;

    const pickupDetails = [];
    if (data.pickup_date) {
      pickupDetails.push(`Ønsket afhentningsdato: ${data.pickup_date}  `);
    }
    if (data.pickup_time) {
      pickupDetails.push(`Ønsket afhentningstidspunkt: ${data.pickup_time}  `);
    }
    const pickupSection = pickupDetails.length > 0 
      ? `\n\nAfhentning:  \n${pickupDetails.join("\n")}` 
      : "";

    const payload = {
      ...restData,
      message: bestilling 
        ? `${orderDetails}${pickupSection}\n\nKommentarer:  \n${bestilling}`
        : `${orderDetails}${pickupSection}`,
      total_price: `${totalPrice} kr.`,
    };

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setFormStatus("success");
        setQuantities({});
        e.target.reset();
      } else {
        setFormStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setFormStatus("error");
    }
  };

  // Group items by category for display
  const categoryOrder = ["Smørrebrød", "Platter", "Sandwich", "Div.", "Burger", "Drikkevarer"];
  const groupedCategories = categoryOrder.map(catName => {
    let items = menuItems.filter(item => item.category === catName);
    if (catName === "Smørrebrød") {
      const nonSild = items.filter(item => !item.name.toLowerCase().includes("sild"));
      const sild = items.filter(item => item.name.toLowerCase().includes("sild"));
      items = [...nonSild, ...sild];
    }
    return {
      title: catName,
      items: items
    };
  }).filter(cat => cat.items.length > 0);

  return (
    <div className="bg-neutral-50 min-h-screen pb-24">
      {/* Page Header */}
      <section className="bg-black text-white py-16 border-b border-neutral-900 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-serif font-bold tracking-tight mb-4">
            Bestilling
          </h1>
          <p className="text-sm text-neutral-400 max-w-lg mx-auto leading-relaxed">
            Vælg de ønskede varer nedenfor, og udfyld dine oplysninger. Vi gør din bestilling klar til afhentning.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col lg:flex-row gap-12">
        {/* Menu Selection */}
        <div className="flex-grow lg:w-2/3">
          {loading ? (
            <div className="flex justify-center items-center py-24">
              <Loader2 className="animate-spin text-neutral-400" size={32} />
            </div>
          ) : (
            <div className="space-y-12">
              {groupedCategories.map((category) => (
                <div key={category.title} className="bg-white p-6 border border-neutral-200 shadow-sm">
                  <h2 className="text-xl font-serif font-bold text-neutral-900 border-b border-neutral-100 pb-4 mb-4">
                    {category.title}
                  </h2>
                  <div className="space-y-4">
                    {category.items.map((item) => {
                      const isSmorrebrod = category.title === "Smørrebrød";
                      const isFish = item.name.toLowerCase().includes("fisk");
                      const handmadPrice = isFish ? 28 : 24;

                      return (
                        <div key={item.id} className="py-4 border-b border-neutral-100 last:border-0">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="pr-4">
                              <h3 className="font-bold text-sm text-neutral-900">
                                {item.name}
                              </h3>
                              {item.name.toLowerCase().includes("sild") && (
                                <p className="text-[11px] text-neutral-500/85 flex items-center gap-1 mt-0.5">
                                  <i className="fa-solid fa-snowflake text-[9px]" /> Sæsonvare: Bestilles typisk op til jul
                                </p>
                              )}
                              {!isSmorrebrod && (
                                <p className="text-xs text-neutral-500 mt-0.5">{item.price} kr.</p>
                              )}
                            </div>

                            {isSmorrebrod ? (
                              <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                                {/* Håndmad Option */}
                                <div className="flex items-center justify-between sm:justify-start gap-4 bg-neutral-50 border border-neutral-200/60 px-3 py-1.5 rounded-sm min-w-[180px]">
                                  <div className="text-xs">
                                    <span className="font-semibold text-neutral-800 block">Håndmad</span>
                                    <span className="text-neutral-500">{handmadPrice} kr.</span>
                                  </div>
                                  <div className="flex items-center gap-2 bg-neutral-200/60 rounded-full px-1.5 py-0.5">
                                    <button
                                      type="button"
                                      onClick={() => handleQuantityChange(item.id, "handmad", -1)}
                                      className="w-5 h-5 flex items-center justify-center rounded-full bg-white text-neutral-600 shadow-sm hover:bg-neutral-50 transition-colors text-xs font-bold"
                                    >
                                      -
                                    </button>
                                    <span className="text-xs font-mono font-bold w-4 text-center">
                                      {quantities[`${item.id}-handmad`] || 0}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleQuantityChange(item.id, "handmad", 1)}
                                      className="w-5 h-5 flex items-center justify-center rounded-full bg-black text-white shadow-sm hover:bg-neutral-800 transition-colors text-xs font-bold"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>

                                {/* Luksus Option */}
                                <div className="flex items-center justify-between sm:justify-start gap-4 bg-neutral-50 border border-neutral-200/60 px-3 py-1.5 rounded-sm min-w-[180px]">
                                  <div className="text-xs">
                                    <span className="font-semibold text-neutral-800 block">Luksus</span>
                                    <span className="text-neutral-500">58 kr.</span>
                                  </div>
                                  <div className="flex items-center gap-2 bg-neutral-200/60 rounded-full px-1.5 py-0.5">
                                    <button
                                      type="button"
                                      onClick={() => handleQuantityChange(item.id, "luksus", -1)}
                                      className="w-5 h-5 flex items-center justify-center rounded-full bg-white text-neutral-600 shadow-sm hover:bg-neutral-50 transition-colors text-xs font-bold"
                                    >
                                      -
                                    </button>
                                    <span className="text-xs font-mono font-bold w-4 text-center">
                                      {quantities[`${item.id}-luksus`] || 0}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleQuantityChange(item.id, "luksus", 1)}
                                      className="w-5 h-5 flex items-center justify-center rounded-full bg-black text-white shadow-sm hover:bg-neutral-800 transition-colors text-xs font-bold"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-3 bg-neutral-100 rounded-full px-2 py-1 self-end md:self-auto">
                                <button
                                  type="button"
                                  onClick={() => handleQuantityChange(item.id, "standard", -1)}
                                  className="w-6 h-6 flex items-center justify-center rounded-full bg-white text-neutral-600 shadow-sm hover:bg-neutral-50 transition-colors"
                                >
                                  -
                                </button>
                                <span className="text-sm font-mono font-bold w-4 text-center">
                                  {quantities[`${item.id}-standard`] || 0}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleQuantityChange(item.id, "standard", 1)}
                                  className="w-6 h-6 flex items-center justify-center rounded-full bg-black text-white shadow-sm hover:bg-neutral-800 transition-colors"
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Order Summary & Form */}
        <div className="lg:w-1/3">
          <div className="bg-white border border-neutral-200 shadow-sm sticky top-24">
            <div className="p-6 border-b border-neutral-100 bg-neutral-50">
              <h2 className="text-lg font-serif font-bold flex items-center gap-2">
                <ShoppingBag size={18} />
                Din Bestilling
              </h2>
            </div>
            
            <div className="p-6">
              {selectedItems.length === 0 ? (
                <p className="text-sm text-neutral-500 text-center py-8">
                  Du har ikke valgt nogen varer endnu.
                </p>
              ) : (
                <div className="space-y-3 mb-6">
                  {selectedItems.map((item) => (
                    <div key={item.key} className="flex justify-between text-sm">
                      <span className="text-neutral-600">
                        {item.quantity}x {item.name}
                      </span>
                      <span className="font-mono font-bold text-neutral-900">
                        {item.price * item.quantity} kr.
                      </span>
                    </div>
                  ))}
                  <div className="pt-4 mt-4 border-t border-neutral-200 flex justify-between items-center">
                    <span className="font-bold text-neutral-900">Total</span>
                    <span className="font-mono text-lg font-bold text-[#2d5a27]">
                      {totalPrice} kr.
                    </span>
                  </div>
                </div>
              )}

              {formStatus === "success" ? (
                <div className="bg-emerald-50 text-emerald-800 p-4 rounded-md flex items-start gap-3">
                  <CheckCircle className="shrink-0 mt-0.5" size={18} />
                  <div>
                    <h4 className="font-bold text-sm">Tak for din bestilling!</h4>
                    <p className="text-xs mt-1">Vi har modtaget din ordre og gør den klar til dig.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot */}
                  <div className="sr-only absolute w-0 h-0 overflow-hidden">
                    <label htmlFor="website_url">Website URL</label>
                    <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1">
                      Navn *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1">
                      Telefonnummer *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1">
                      E-mailadresse *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex flex-col">
                      <label htmlFor="pickup_date" className="text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1 min-h-[2.25rem] flex flex-col justify-end">
                        Ønsket afhentningsdato *
                      </label>
                      <input
                        type="date"
                        id="pickup_date"
                        name="pickup_date"
                        required
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full h-10 px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black bg-white"
                      />
                    </div>

                    <div className="flex flex-col">
                      <label htmlFor="pickup_time" className="text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1 min-h-[2.25rem] flex flex-col justify-end">
                        Ønsket tidspunkt *
                      </label>
                      <input
                        type="time"
                        id="pickup_time"
                        name="pickup_time"
                        required
                        className="w-full h-10 px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="bestilling" className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1">
                      Evt. kommentarer
                    </label>
                    <textarea
                      id="bestilling"
                      name="bestilling"
                      rows="2"
                      className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                    ></textarea>
                  </div>

                  {formStatus === "error" && (
                    <div className="bg-red-50 text-red-800 p-3 text-xs flex items-center gap-2">
                      <AlertCircle size={14} />
                      Der opstod en fejl. Prøv venligst igen.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={selectedItems.length === 0 || formStatus === "submitting"}
                    className="w-full py-3 bg-[#2d5a27] hover:bg-[#20401c] text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                  >
                    {formStatus === "submitting" ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Sender...
                      </>
                    ) : (
                      "Gennemfør bestilling"
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
